/* eslint-disable no-restricted-globals */
import URI from 'urijs';
import { useCallback, useMemo } from 'react';
import {
  invokeAuthenticationPopup,
  fetchTokenExchange,
  fetchTokenWithAuthenticationPopup,
} from '@bingads-webui-react/authentication-popup';
import { ILogger, getRootUrl } from '@bingads-webui-react/copilot-utils';
import {
  LayoutMode, LOG_SCENARIO, fakeLogger,
} from './utils';

const TOKEN_FETCH_RETRY_COUNT = 3;

const getFetchTokenURL = () => {
  const rootUrl = getRootUrl();
  return `${rootUrl}/tokenExchange/copilot`;
};

export function fetchAppBasedChannelToken() {
  const rootUrl = getRootUrl();
  const url = `${rootUrl}/tokenExchange/qna`;

  return fetchTokenExchange(url, TOKEN_FETCH_RETRY_COUNT);
}

let msaPopUpOpened = false;
const getIsMagicCodeSignIn = () => new URLSearchParams(window.location.search).get('MagicCodeSignIn') === 'true';

const getPopupRedirectionUrl = () => {
  const pagePath = new URL(window.location.href).pathname.split('/')[1];
  return `/${pagePath}/closepopup`;
};

const getLoginPopupUrl = ({ userName, localeId, isAADUser }: { userName: string, localeId: number | string, isAADUser: boolean }) => {
  const rootUrl = getRootUrl();
  const redirectionUrl = getPopupRedirectionUrl();
  const loginPopupUrl = URI.build({
    path: `${rootUrl}/login/federated`,
    query: URI.buildQuery({
      idp: isAADUser ? 'aad' : 'msav2', // we might need to change for AAD users
      uname: userName,
      lcid: localeId,
      rurl: `${rootUrl}${redirectionUrl}`,
    }),
  });
  return loginPopupUrl;
};

export const useTokenExchange = ({
  userName, localeId, isAADUser, logger, onAuthPopupError, shouldRetry = false,
} = {
  userName: '',
  localeId: '',
  isAADUser: false,
  logger: fakeLogger,
  onAuthPopupError: () => {},
  shouldRetry: false,
}) => {
  const loginPopupUrl = getLoginPopupUrl({
    userName,
    localeId,
    isAADUser,
  });
  const fetchTokenUrl = getFetchTokenURL();
  const popupRedirectUrl = getPopupRedirectionUrl();

  const bypassAuthPopup = getIsMagicCodeSignIn();

  const onAuthPopupSuccess = useCallback(() => () => {
    msaPopUpOpened = true;
  }, []);

  const fetchTokenWithMSASignIn = useMemo(
    () => () => fetchTokenWithAuthenticationPopup(
      fetchTokenUrl,
      loginPopupUrl,
      popupRedirectUrl,
      bypassAuthPopup,
      LOG_SCENARIO,
      onAuthPopupSuccess,
      onAuthPopupError,
      logger,
      shouldRetry ? TOKEN_FETCH_RETRY_COUNT : 0,
    ),
    [
      logger,
      fetchTokenUrl,
      loginPopupUrl,
      popupRedirectUrl,
      bypassAuthPopup,
      onAuthPopupSuccess,
      onAuthPopupError,
      shouldRetry,
    ],
  );

  return fetchTokenWithMSASignIn;
};

export const toggleCopilotWithMSASignInPopup = ({
  userName,
  localeId,
  isAADUser,
  isAppBasedAuthEnabled,
  toggleCopilot,
  logger = fakeLogger,
  onAuthPopupError = () => {},
}: {
  userName: string,
  localeId: string | number;
  isAADUser: boolean,
  isAppBasedAuthEnabled: boolean,
  toggleCopilot: (mode: LayoutMode) => void;
  logger: ILogger,
  onAuthPopupError: () => void;
}) => {
  const loginPopupUrl = getLoginPopupUrl({ userName, localeId, isAADUser });
  const redirectionUrl = getPopupRedirectionUrl();

  const onClick = (mode: LayoutMode) => {
    logger.trace(`[Copilot] Toggle copilot, params: ${JSON.stringify({
      userName,
      localeId,
      isAADUser,
      isAppBasedAuthEnabled,
      mode,
    })}`);
    if (
      !isAADUser
      && !isAppBasedAuthEnabled
      && !getIsMagicCodeSignIn()
      && mode === 'expanded'
      && !msaPopUpOpened
    ) {
      logger.trace(`[Copilot] MSA sign in started, loginPopupUrl: ${loginPopupUrl}`);
      invokeAuthenticationPopup(
        loginPopupUrl,
        () => {
          msaPopUpOpened = true;
          toggleCopilot(mode);

          logger.trace('[Copilot] MSA sign in suceeded');
        },
        (error: any) => {
          logger.error(`[Copilot] MSA sign in failed. error: ${JSON.stringify(error)}`);
          onAuthPopupError();
        },
        redirectionUrl,
      );
    } else {
      toggleCopilot(mode);
    }
  };

  return onClick;
};

export const fetchLiveAgentToken = async <TQueue extends string>({
  accountId,
  locale,
  queue,
  logger = fakeLogger,
}: {
  accountId?: string,
  locale: string;
  logger?: ILogger
  queue: TQueue;
}) => {
  const rootUrl = getRootUrl();
  const liveAgentUrl = URI.build({
    path: `${rootUrl}/tokenExchange/liveagent`,
    query: URI.buildQuery({
      accountId,
      locale,
      queue,
    }),
  });

  try {
    const { token } = await fetchTokenExchange(liveAgentUrl);
    return token;
  } catch (error) {
    logger.error(`Failed to fetch live agent token. error: ${JSON.stringify(error)}`);
    return null;
  }
};
